import { useEffect } from 'react';
import { Link, useParams, useLocation, Navigate } from 'react-router-dom';
import { articles, type ArticleSection, type Article } from '@/mocks/articles';

const articleHeaderImages: Record<string, string> = {
  'sugargoo-spreadsheet-complete-guide': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20clean%20organized%20desk%20workspace%20with%20dual%20monitors%20displaying%20spreadsheet%20data%20and%20product%20images%2C%20fashion%20items%20neatly%20arranged%2C%20bright%20natural%20daylight%2C%20modern%20home%20office%2C%20warm%20inviting%20aesthetic%2C%20editorial%20style&width=1600&height=600&seq=article-header-complete-guide&orientation=landscape',
  'sugargoo-review-is-legit': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20person%20happily%20unboxing%20a%20package%20on%20a%20clean%20white%20desk%2C%20with%20shopping%20app%20displayed%20on%20a%20laptop%20in%20the%20background%2C%20bright%20natural%20light%2C%20genuine%20joyful%20expression%2C%20modern%20home%20setting%2C%20editorial%20product%20review%20photography&width=1600&height=600&seq=article-header-review-legit&orientation=landscape',
  'sugargoo-shipping-cost-guide': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20multiple%20packages%20and%20shipping%20boxes%20on%20a%20clean%20industrial%20table%2C%20with%20a%20world%20map%20on%20the%20wall%20behind%20showing%20international%20shipping%20routes%2C%20warm%20professional%20lighting%2C%20modern%20logistics%20center%20aesthetic&width=1600&height=600&seq=article-header-shipping-cost&orientation=landscape',
  'sugargoo-coupon-discount-code': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20colorful%20discount%20tags%20and%20shopping%20vouchers%20arranged%20elegantly%20on%20a%20light%20marble%20surface%2C%20with%20a%20modern%20tablet%20showing%20an%20ecommerce%20app%2C%20bright%20natural%20light%2C%20clean%20premium%20aesthetic&width=1600&height=600&seq=article-header-coupon-code&orientation=landscape',
  'sugargoo-warehouse-guide': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20clean%20organized%20fulfillment%20center%20with%20labeled%20packages%20on%20shelves%20and%20workers%20handling%20boxes%20professionally%2C%20modern%20warehouse%20interior%2C%20bright%20efficient%20lighting%2C%20professional%20logistics%20environment&width=1600&height=600&seq=article-header-warehouse&orientation=landscape',
  'sugargoo-haul-building-guide': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20beautifully%20laid%20out%20flat%20lay%20of%20fashion%20items%20including%20sneakers%2C%20hoodies%2C%20t-shirts%2C%20and%20accessories%20arranged%20on%20a%20light%20wooden%20surface%2C%20bright%20natural%20daylight%2C%20editorial%20lifestyle%20photography&width=1600&height=600&seq=article-header-haul-build&orientation=landscape',
  'sugargoo-beginner-tutorial': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20welcoming%20home%20office%20setup%20with%20a%20large%20monitor%20displaying%20an%20ecommerce%20platform%20interface%2C%20a%20notebook%20with%20shopping%20notes%2C%20a%20smartphone%20nearby%2C%20warm%20natural%20light%2C%20cozy%20and%20approachable%20vibe&width=1600&height=600&seq=article-header-beginner&orientation=landscape',
  'sugargoo-qc-photos-guide': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20professional%20product%20photography%20station%20with%20a%20camera%20on%20tripod%2C%20lighting%20equipment%2C%20and%20products%20arranged%20on%20a%20clean%20white%20surface%2C%20modern%20studio%20environment%2C%20crisp%20professional%20lighting&width=1600&height=600&seq=article-header-qc-photos&orientation=landscape',
  'sugargoo-budget-finds-guide': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20beautiful%20flat%20lay%20showcasing%20budget-friendly%20fashion%20finds%20including%20sneakers%2C%20t-shirts%2C%20caps%2C%20and%20accessories%20neatly%20arranged%20on%20a%20warm%20wooden%20surface%2C%20bright%20natural%20light%2C%20editorial%20bargain%20shopping%20aesthetic&width=1600&height=600&seq=article-header-budget-finds&orientation=landscape',
  'sugargoo-vs-other-agents': 'https://readdy.ai/api/search-image?query=Wide%20banner%20shot%20of%20a%20desk%20with%20multiple%20devices%20showing%20different%20ecommerce%20platforms%2C%20a%20notebook%20with%20comparison%20notes%2C%20clean%20modern%20workspace%2C%20bright%20organized%20atmosphere%2C%20professional%20comparison%20review%20aesthetic&width=1600&height=600&seq=article-header-vs-agents&orientation=landscape',
};

function SectionRenderer({ section }: { section: ArticleSection }) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4 mt-8">
          {section.heading}
        </h2>
      );

    case 'text':
      return (
        <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-3">
          {section.content}
        </p>
      );

    case 'list':
      return (
        <div className="my-5">
          {section.heading && (
            <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-3">{section.heading}</h4>
          )}
          <ul className="list-disc pl-5 space-y-2 text-foreground-700 text-sm md:text-base">
            {section.items?.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      );

    case 'table':
      return (
        <div className="my-5">
          {section.heading && (
            <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-3">{section.heading}</h4>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-background-300">
                  {section.table?.headers.map((h, idx) => (
                    <th key={idx} className="py-3 px-3 text-xs md:text-sm font-semibold text-foreground-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.table?.rows.map((row, rIdx) => (
                  <tr key={rIdx} className="border-b border-background-200/70">
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className={`py-2.5 px-3 text-sm ${cIdx === 0 ? 'text-foreground-700 font-medium' : 'text-foreground-600'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'callout':
      const bgClass = section.calloutType === 'tip' ? 'bg-accent-50 border-accent-200/50' : section.calloutType === 'warning' ? 'bg-primary-50 border-primary-200/50' : 'bg-secondary-50 border-secondary-200/50';
      const textClass = section.calloutType === 'tip' ? 'text-accent-900' : section.calloutType === 'warning' ? 'text-primary-900' : 'text-secondary-900';
      const bodyClass = section.calloutType === 'tip' ? 'text-accent-800' : section.calloutType === 'warning' ? 'text-primary-800' : 'text-secondary-800';
      const iconName = section.calloutType === 'tip' ? 'ri-lightbulb-line' : section.calloutType === 'warning' ? 'ri-alert-line' : 'ri-information-line';
      return (
        <div className={`border rounded-xl p-5 my-5 ${bgClass}`}>
          <h4 className={`font-heading text-base mb-2 flex items-center gap-2 ${textClass}`}>
            <i className={iconName}></i>
            {section.heading}
          </h4>
          <p className={`text-sm ${bodyClass}`}>{section.content}</p>
        </div>
      );

    case 'steps':
      return (
        <div className="space-y-5 my-5">
          {section.steps?.map((step, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading text-sm md:text-base">
                {idx + 1}
              </div>
              <div>
                <h4 className="font-semibold text-foreground-900 text-sm md:text-base">{step.title}</h4>
                <p className="text-foreground-600 text-xs md:text-sm mt-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case 'faq':
      return (
        <section className="bg-background-100 border border-background-200/60 rounded-2xl p-5 md:p-8 my-8">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
            Frequently Asked Questions About Sugargoo Spreadsheet
          </h2>
          <div className="space-y-5">
            {section.faqs?.map((faq, idx) => (
              <div key={idx}>
                <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-1.5">{faq.question}</h4>
                <p className="text-foreground-600 text-xs md:text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className="text-center py-8 my-4">
          <h3 className="font-heading text-xl text-foreground-900 mb-3">
            Ready to Start Shopping Smarter?
          </h3>
          <p className="text-foreground-600 text-sm md:text-base mb-5 max-w-md mx-auto">
            Access our complete Sugargoo Spreadsheet database and discover thousands of verified finds across all categories.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Sugargoo Spreadsheet
            </a>
            <Link
              to="/"
              className="w-full sm:w-auto px-8 py-3 border border-primary-300 text-primary-600 rounded-full text-sm md:text-base font-semibold hover:bg-primary-50 transition-all whitespace-nowrap"
            >
              Back to Home
            </Link>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const effectiveSlug = slug || (location.pathname === '/sugargoo-spreadsheet-complete-guide' ? 'sugargoo-spreadsheet-complete-guide' : undefined);
  const article: Article | undefined = articles.find((a) => a.slug === effectiveSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveSlug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const canonicalUrl = `https://sugargoosheet.forum/article/${article.slug}`;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: articleHeaderImages[article.slug] || articleHeaderImages['sugargoo-spreadsheet-complete-guide'],
    datePublished: article.updatedDate,
    dateModified: article.updatedDate,
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Sheet Team',
      url: 'https://sugargoosheet.forum/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Sheet',
      url: 'https://sugargoosheet.forum/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://sugargoosheet.forum/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: 'https://sugargoosheet.forum/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: canonicalUrl,
      },
    ],
  };

  const faqSection = article.sections.find((s) => s.type === 'faq');
  const faqJsonLd = faqSection?.faqs ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSection.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  } : null;

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-4xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="font-heading text-lg">Sugargoo Sheet</span>
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-xs md:text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Browse Spreadsheets
          </a>
        </div>
      </nav>

      <header className="relative overflow-hidden bg-background-300">
        <div className="absolute inset-0">
          <img
            src={articleHeaderImages[article.slug] || articleHeaderImages['sugargoo-spreadsheet-complete-guide']}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/50"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 md:px-6 py-20 md:py-28 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-background-50/80 mb-3">
            <Link to="/" className="hover:text-background-50 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50/60">{article.category}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl text-background-50 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-background-50/85 text-sm md:text-base leading-relaxed mb-4 max-w-2xl mx-auto">
            {article.description}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-background-50/70">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> {article.updatedDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> By Sugargoo Sheet Team
            </span>
          </div>
        </div>
      </header>

      <article className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="prose-custom space-y-2">
            {article.sections.map((section, idx) => (
              <SectionRenderer key={idx} section={section} />
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200">
            <h3 className="font-heading text-lg text-foreground-900 mb-4">
              Related Guides
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="flex items-center gap-3 p-3 bg-background-100 border border-background-200/60 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-file-text-line text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700 hover:text-primary-600 transition-colors line-clamp-1">
                      {related.title}
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-secondary-100 text-foreground-700">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 text-center">
          <p className="text-sm font-heading text-foreground-900 mb-2">
            <Link to="/" className="hover:text-primary-500 transition-colors">Sugargoo Sheet</Link>
          </p>
          <p className="text-xs text-foreground-500 leading-relaxed mb-3">
            <strong>Disclaimer:</strong> Sugargoo Sheet is an independent information platform and is not affiliated with Sugargoo.
            We provide educational resources and curated shopping links to help you make informed decisions.
          </p>
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} Sugargoo Sheet. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
}